<div class="game-details-card" [attr.aria-busy]="saving">
  <div class="game-details-card__overlay" *ngIf="saving">
    <mat-spinner diameter="32"></mat-spinner>
  </div>

  <div
    class="schedule-correction-banner"
    *ngIf="game.pendingScheduleCorrection as correction"
  >
    <mat-icon class="schedule-correction-banner__icon">event_note</mat-icon>
    <span>
      Pending schedule correction from customer:
      <ng-container *ngIf="correctionDateDisplay">
        new date <strong>{{ correctionDateDisplay }}</strong></ng-container
      ><ng-container
        *ngIf="correctionDateDisplay && correction.correctionVenueName"
        >,</ng-container
      >
      <ng-container *ngIf="correction.correctionVenueName">
        new venue <strong>{{ correction.correctionVenueName }}</strong>
      </ng-container>
    </span>
    <button
      mat-raised-button
      color="primary"
      type="button"
      [disabled]="applyingScheduleCorrection"
      (click)="applyScheduleCorrection()"
    >
      Apply Correction
    </button>
  </div>

  <div
    class="schedule-correction-warning"
    *ngIf="game.pendingScheduleCorrectionUnavailable"
  >
    <mat-icon class="schedule-correction-warning__icon">error_outline</mat-icon>
    <span>
      Couldn't check for pending schedule corrections. Refresh to try again.
    </span>
  </div>

  <div class="row">
    <div class="col-2">
      <label>
        Venue Date
        <mat-icon
          class="info-icon"
          [matTooltip]="usersLocalDatePreview ? 'Local game date: ' + usersLocalDatePreview + ' ' + usersLocalTimezoneShortName : ''"
          >info_outline</mat-icon
        >
      </label>
      <input
        name="date"
        class="form-control"
        [ngModel]="venueLocalDate"
        (ngModelChange)="onVenueDateChange($event)"
        placeholder="2017-09-30"
        type="datetime-local"
        required
        #date="ngModel"
        [class.is-invalid]="date.invalid && (date.dirty || date.touched)"
      />
      <div class="invalid-feedback">
        Please provide a date. But first select a timezone.
      </div>
    </div>

    <div class="col-1">
      <label matTooltip="Venue's UTC timezone offset in hours">
        Venue Offset
      </label>
      <input
        name="venue_utc_offset"
        class="form-control"
        readonly
        [ngModel]="game.venueUTCOffset"
      />
    </div>

    <div class="col-2">
      <label>
        Venue's Local Timezone
        <mat-icon
          class="info-icon"
          matTooltip="Search by timezone (e.g. 'Zurich'), city name (e.g. 'Bern'), or UTC offset (e.g. '+01' or 'UTC-05')"
          >info_outline</mat-icon
        >
      </label>
      <input
        type="text"
        class="form-control"
        placeholder="Search by city, timezone, or offset"
        [formControl]="timezoneControl"
        [matAutocomplete]="tzAuto"
        (focus)="showTimezoneSuggestions()"
      />
      <mat-autocomplete
        #tzAuto="matAutocomplete"
        (optionSelected)="onTimezoneSelected($event.option.value)"
      >
        <mat-option
          *ngFor="let tz of filteredTimezones$ | async"
          [value]="tz.timezoneId"
        >
          {{ tz.label }}
        </mat-option>
      </mat-autocomplete>
    </div>

    <div class="col-2">
      <label>Venue Name</label>
      <input
        name="venue_name"
        class="form-control"
        [(ngModel)]="game.venueName"
        (ngModelChange)="onEssentialsFieldChange()"
      />
    </div>

    <div class="col-1">
      <label>Season</label>
      <select
        [(ngModel)]="game.season"
        name="season"
        class="form-control"
        required
        #season="ngModel"
        [class.is-invalid]="season.invalid && (season.dirty || season.touched)"
        (ngModelChange)="seasonChange.emit($event)"
      >
        <option *ngFor="let season of seasons" [value]="season">
          {{ season }}
        </option>
      </select>
      <div class="invalid-feedback">Please provide a season.</div>
    </div>

    <div class="col-3">
      <label>Season Type</label>
      <select
        [(ngModel)]="game.seasonType"
        name="seasonType"
        class="form-control"
        required
        #seasonType="ngModel"
        (ngModelChange)="onEssentialsFieldChange()"
        [class.is-invalid]="
          seasonType.invalid && (seasonType.dirty || seasonType.touched)
        "
      >
        <option *ngFor="let seasonType of seasonTypes" [value]="seasonType">
          {{ seasonType }}
        </option>
      </select>
      <div class="invalid-feedback">Please provide a season type.</div>
    </div>
  </div>
  <div class="row mt-3">
    <div class="col-1">
      <label>ID Type</label>
      <select
        [(ngModel)]="game.idType"
        name="idType"
        class="form-control"
        (ngModelChange)="idTypeChange.emit($event)"
      >
        <option *ngFor="let idType of idTypes" [value]="idType">
          {{ idType }}
        </option>
      </select>
    </div>

    <div class="col-1" *ngIf="game.idType === 'SIHF'">
      <label>SIHF ID</label>

      <input
        class="form-control"
        [(ngModel)]="game.gameId"
        (ngModelChange)="onEssentialsFieldChange()"
        placeholder="SIHF ID"
        required
      />
    </div>

    <div class="col-3">
      <label>League</label>
      <select
        name="league"
        class="form-control"
        [(ngModel)]="game.league"
        (ngModelChange)="leagueChange.emit($event)"
        required
        #league="ngModel"
        [class.is-invalid]="league.invalid && (league.dirty || league.touched)"
        [disabled]="!game.season"
      >
        <option [ngValue]="null">Select League</option>
        <option *ngFor="let league of leagues" [ngValue]="league.sihfId">
          {{ league.shortName }} – {{ league.fullName }}
        </option>
      </select>
      <div class="invalid-feedback">Please provide a league.</div>
    </div>

    <div class="col-3">
      <app-team-input
        [placeholder]="'Home Team'"
        [idType]="game.idType"
        [shortName]="game.homeTeam"
        [teamId]="game.homeTeamId"
        [teams]="teams"
        (teamChange)="homeTeamChange.emit($event)"
        [disabled]="teamChangeDisabled"
      ></app-team-input>
    </div>

    <div class="col-3">
      <app-team-input
        [placeholder]="'Away Team'"
        [idType]="game.idType"
        [shortName]="game.awayTeam"
        [teamId]="game.awayTeamId"
        [teams]="teams"
        (teamChange)="awayTeamChange.emit($event)"
        [disabled]="teamChangeDisabled"
      ></app-team-input>
    </div>
  </div>
  <div class="row mt-3">
    <div class="col-2">
      <label>Period Length (min)</label>
      <input
        [(ngModel)]="game.periodLengthInMinutes"
        (ngModelChange)="onEssentialsFieldChange()"
        name="periodLengthInMinutes"
        class="form-control"
        type="number"
        min="0"
      />
    </div>
    <div class="col-2">
      <label>Overtime Length (min)</label>
      <input
        [(ngModel)]="game.overtimeLengthInMinutes"
        (ngModelChange)="onEssentialsFieldChange()"
        name="overtimeLengthInMinutes"
        class="form-control"
        type="number"
        min="0"
        [placeholder]="game.overtimeLength ?? ''"
      />
    </div>
  </div>
</div>
